/**
 * Roster Page — Shows all current assignments across battalions,
 * stations, and units.
 *
 * - All authenticated members can view the roster.
 * - Officers+ can propose changes (scoped to their battalion).
 * - All changes require Fire Chief approval.
 *
 * Structure: 5 Battalions + SOD
 * Each battalion has 4 stations (510, 513, 518, 520) + command section.
 * Each station has Engine, Ambulance, Utility (+ Rescue 510, Tower 513).
 */

import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../hooks/useAuth';
import { isOfficerOrAbove, isAdmin as isAdminRole } from '../lib/auth';
import { useRoster } from '../hooks/useRoster';
import { useMemberPhotos } from '../hooks/useMemberPhotos';
import { computeBattalionForDate, getTodayDateStr } from '../lib/schedule';
import AppLayout from '../components/layout/AppLayout';
import { BATTALIONS, SOD, ALL_STATIONS, type BattalionDef, type UnitDef } from '../../../shared/roster-config';
import ApparatusCard, { isApparatusUnit } from '../components/roster/ApparatusView';
import {
  RefreshCw,
  MapPin,
  User,
  Shield,
  Clock,
  Check,
  X,
  ChevronDown,
  ChevronRight,
  ClipboardList,
  Search,
  Filter,
} from 'lucide-react';

// ── Battalion text colors (match duty calendar) ──────────────────────

const BATTALION_COLORS: Record<number, string> = {
  1: '#4DD0E1',
  2: '#AED581',
  3: '#EF9A9A',
  4: '#FFB74D',
  5: '#9FA8DA',
};

const BATTALION_BG_COLORS: Record<number, string> = {
  1: 'rgba(77, 208, 225, 0.08)',
  2: 'rgba(174, 213, 129, 0.08)',
  3: 'rgba(239, 154, 154, 0.08)',
  4: 'rgba(255, 183, 77, 0.08)',
  5: 'rgba(159, 168, 218, 0.08)',
};

// ── Main Component ───────────────────────────────────────────────────

// ── Unit type options for the filter dropdown ───────────────────────
const UNIT_TYPE_OPTIONS: { value: UnitDef['type']; label: string }[] = [
  { value: 'engine', label: 'Engine' },
  { value: 'ambulance', label: 'Ambulance' },
  { value: 'tower', label: 'Tower' },
  { value: 'rescue', label: 'Rescue' },
  { value: 'buggy', label: 'BC Buggy' },
];

export default function Roster() {
  const { user } = useAuth();
  const roster = useRoster();
  // Auto-expand the user's own battalion, or today's on-duty battalion as fallback
  const [expandedBattalions, setExpandedBattalions] = useState<Set<number>>(() => {
    const userBn = user?.battalion?.id;
    if (userBn) return new Set([userBn]);
    const todayBn = computeBattalionForDate(getTodayDateStr()).battalion;
    return new Set([todayBn]);
  });
  const [showSOD, setShowSOD] = useState(false);
  const [showPendingChanges, setShowPendingChanges] = useState(false);
  const [editingSeat, setEditingSeat] = useState<{
    battalionId: number;
    unitId: string;
    seatId: string;
  } | null>(null);
  const [selectedMemberId, setSelectedMemberId] = useState<string>('');
  const [proposalNotes, setProposalNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Filter & search state
  const [filterBattalion, setFilterBattalion] = useState<string>('');
  const [filterStation, setFilterStation] = useState<string>('');
  const [filterUnitType, setFilterUnitType] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');

  const isOfficerOrAdmin = isOfficerOrAbove(user);
  const isAdmin = isAdminRole(user);

  // Collect all assigned member IDs for photo loading
  const assignedMemberIds = useMemo(() => roster.assignments.map((a) => a.member_id), [roster.assignments]);
  const memberPhotos = useMemberPhotos(assignedMemberIds);

  // Load pending changes and members for officers
  useEffect(() => {
    if (isOfficerOrAdmin) {
      roster.fetchPendingChanges();
      roster.fetchMembers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOfficerOrAdmin]);

  // When battalion filter changes, auto-expand it
  useEffect(() => {
    if (filterBattalion) {
      setExpandedBattalions(new Set([parseInt(filterBattalion, 10)]));
    }
  }, [filterBattalion]);

  const hasActiveFilters = filterBattalion || filterStation || filterUnitType || searchQuery;

  function toggleBattalion(id: number) {
    setExpandedBattalions((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function canEditBattalion(battalionId: number): boolean {
    if (isAdmin) return true;
    if (isOfficerOrAdmin && !isAdmin && user?.battalion?.id === battalionId) return true;
    return false;
  }

  async function handlePropose() {
    if (!editingSeat) return;
    setSubmitting(true);
    try {
      await roster.proposeChange({
        battalion_id: editingSeat.battalionId,
        unit_id: editingSeat.unitId,
        seat_id: editingSeat.seatId,
        proposed_member_id: selectedMemberId ? parseInt(selectedMemberId, 10) : null,
        notes: proposalNotes || undefined,
      });
      setEditingSeat(null);
      setSelectedMemberId('');
      setProposalNotes('');
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to submit change');
    } finally {
      setSubmitting(false);
    }
  }

  if (roster.loading) {
    return (
      <AppLayout title="Roster">
        <div className="flex items-center justify-center min-h-[60dvh] text-content-tertiary text-base">
          Loading roster...
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout title="Roster">
      <div className="w-full max-w-[1400px] mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mt-3 mb-5">
          <div>
            <h1 className="text-lg font-bold text-content-primary">Roster Assignments</h1>
            <p className="text-xs text-content-tertiary mt-0.5">All battalion and station personnel assignments</p>
          </div>
          <div className="flex items-center gap-2">
            {isOfficerOrAdmin && roster.pendingChanges.length > 0 && (
              <button
                type="button"
                onClick={() => setShowPendingChanges(!showPendingChanges)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold
                           text-warning-400 bg-warning-500/10 border border-warning-500/30
                           rounded-lg cursor-pointer hover:bg-warning-500/20 transition-colors duration-150"
              >
                <Clock className="h-3.5 w-3.5" />
                {roster.pendingChanges.length} Pending
              </button>
            )}
            <button
              type="button"
              onClick={roster.fetchRoster}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-content-secondary
                         bg-surface-raised border border-edge-default rounded-lg cursor-pointer
                         hover:bg-surface-sunken transition-colors duration-150"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Refresh
            </button>
          </div>
        </div>

        {/* Filter bar */}
        <div className="mb-4 bg-surface-raised rounded-xl border border-edge-default p-3">
          <div className="flex flex-wrap items-center gap-2">
            <Filter className="h-3.5 w-3.5 text-content-tertiary shrink-0" />
            {/* Battalion filter */}
            <select
              value={filterBattalion}
              onChange={(e) => setFilterBattalion(e.target.value)}
              className="px-2.5 py-1.5 text-xs bg-surface-base border border-edge-default rounded-lg
                         text-content-primary focus:outline-none focus:ring-1 focus:ring-action-primary"
              aria-label="Filter by battalion"
            >
              <option value="">All Battalions</option>
              {BATTALIONS.map((bn) => (
                <option key={bn.id} value={String(bn.id)}>
                  Battalion {bn.id}
                </option>
              ))}
            </select>
            {/* Station filter */}
            <select
              value={filterStation}
              onChange={(e) => setFilterStation(e.target.value)}
              className="px-2.5 py-1.5 text-xs bg-surface-base border border-edge-default rounded-lg
                         text-content-primary focus:outline-none focus:ring-1 focus:ring-action-primary"
              aria-label="Filter by station"
            >
              <option value="">All Stations</option>
              {ALL_STATIONS.map((st) => (
                <option key={st.id} value={st.id}>
                  {st.label}
                </option>
              ))}
            </select>
            {/* Unit type filter */}
            <select
              value={filterUnitType}
              onChange={(e) => setFilterUnitType(e.target.value)}
              className="px-2.5 py-1.5 text-xs bg-surface-base border border-edge-default rounded-lg
                         text-content-primary focus:outline-none focus:ring-1 focus:ring-action-primary"
              aria-label="Filter by unit type"
            >
              <option value="">All Unit Types</option>
              {UNIT_TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            {/* Search */}
            <div className="relative flex-1 min-w-[180px]">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-content-tertiary" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search name, unit, or position..."
                className="w-full pl-8 pr-3 py-1.5 text-xs bg-surface-base border border-edge-default rounded-lg
                           text-content-primary placeholder:text-content-disabled
                           focus:outline-none focus:ring-1 focus:ring-action-primary"
                aria-label="Search roster"
              />
            </div>
            {/* Clear filters */}
            {hasActiveFilters && (
              <button
                type="button"
                onClick={() => {
                  setFilterBattalion('');
                  setFilterStation('');
                  setFilterUnitType('');
                  setSearchQuery('');
                }}
                className="px-2.5 py-1.5 text-xs font-medium text-content-secondary
                           bg-surface-sunken border border-edge-default rounded-lg cursor-pointer
                           hover:bg-surface-base transition-colors duration-150"
              >
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Error */}
        {roster.error && (
          <div className="px-4 py-3 bg-danger-500/15 border border-danger-500/30 rounded-lg text-sm text-danger-400 mb-4">
            {roster.error}
          </div>
        )}

        {/* Pending Changes Panel */}
        {showPendingChanges && roster.pendingChanges.length > 0 && (
          <PendingChangesPanel
            changes={roster.pendingChanges}
            isAdmin={isAdmin}
            onApprove={roster.approveChange}
            onReject={roster.rejectChange}
            onClose={() => setShowPendingChanges(false)}
          />
        )}

        {/* Battalion sections */}
        <div className="space-y-3">
          {BATTALIONS.filter((bn) => !filterBattalion || bn.id === parseInt(filterBattalion, 10)).map((battalion) => (
            <BattalionSection
              key={battalion.id}
              battalion={battalion}
              expanded={expandedBattalions.has(battalion.id)}
              onToggle={() => toggleBattalion(battalion.id)}
              getAssignment={roster.getAssignment}
              getPendingChange={roster.getPendingChange}
              canEdit={canEditBattalion(battalion.id)}
              onEditSeat={(unitId, seatId) => setEditingSeat({ battalionId: battalion.id, unitId, seatId })}
              filterStation={filterStation}
              filterUnitType={filterUnitType}
              searchQuery={searchQuery}
              memberPhotos={memberPhotos}
            />
          ))}

          {/* SOD Section */}
          <div className="bg-surface-raised rounded-xl border border-edge-default overflow-hidden">
            <button
              type="button"
              onClick={() => setShowSOD(!showSOD)}
              className="w-full flex items-center gap-3 px-4 py-3 cursor-pointer
                         bg-surface-sunken hover:bg-surface-base transition-colors duration-150 text-left border-none"
            >
              {showSOD ? (
                <ChevronDown className="h-4 w-4 text-content-secondary shrink-0" />
              ) : (
                <ChevronRight className="h-4 w-4 text-content-secondary shrink-0" />
              )}
              <Shield className="h-4 w-4 text-action-primary shrink-0" />
              <span className="text-sm font-bold text-content-primary">{SOD.label}</span>
              <span className="text-xs text-content-tertiary ml-auto">{roster.sodAssignments.length} assigned</span>
            </button>
            {showSOD && (
              <div className="p-4">
                {roster.sodAssignments.length > 0 ? (
                  <div className="grid gap-2 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
                    {roster.sodAssignments.map((a) => (
                      <div
                        key={a.id}
                        className="flex items-center gap-2 px-3 py-2 bg-surface-base rounded-lg border border-edge-muted"
                      >
                        <div className="w-6 h-6 rounded-full bg-action-primary/15 flex items-center justify-center shrink-0">
                          <User className="h-3.5 w-3.5 text-action-primary" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-content-primary truncate">{a.display_name}</div>
                          <div className="text-[10px] text-content-tertiary truncate">{a.role_label}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-sm text-content-tertiary italic text-center py-4">No SOD assignments</div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Edit Seat Modal */}
        {editingSeat && (
          <EditSeatModal
            battalionId={editingSeat.battalionId}
            unitId={editingSeat.unitId}
            seatId={editingSeat.seatId}
            currentAssignment={roster.getAssignment(editingSeat.battalionId, editingSeat.unitId, editingSeat.seatId)}
            members={roster.members}
            selectedMemberId={selectedMemberId}
            onSelectMember={setSelectedMemberId}
            notes={proposalNotes}
            onNotesChange={setProposalNotes}
            submitting={submitting}
            onSubmit={handlePropose}
            onClose={() => {
              setEditingSeat(null);
              setSelectedMemberId('');
              setProposalNotes('');
            }}
          />
        )}
      </div>
    </AppLayout>
  );
}

// ── Battalion Section ────────────────────────────────────────────────

/** Check if a unit has any seat matching the search query */
function unitMatchesSearch(
  unit: UnitDef,
  battalionId: number,
  query: string,
  getAssignment: (bn: number, unit: string, seat: string) => { display_name: string; member_id: number } | undefined,
): boolean {
  if (!query) return true;
  const q = query.toLowerCase();
  // Match unit ID or label
  if (unit.id.toLowerCase().includes(q) || unit.label.toLowerCase().includes(q)) return true;
  // Match seat labels (riding positions)
  if (unit.seats.some((s) => s.label.toLowerCase().includes(q))) return true;
  // Match assigned member names
  if (unit.seats.some((s) => getAssignment(battalionId, unit.id, s.id)?.display_name.toLowerCase().includes(q)))
    return true;
  return false;
}

function BattalionSection({
  battalion,
  expanded,
  onToggle,
  getAssignment,
  getPendingChange,
  canEdit,
  onEditSeat,
  filterStation,
  filterUnitType,
  searchQuery,
  memberPhotos,
}: {
  battalion: BattalionDef;
  expanded: boolean;
  onToggle: () => void;
  getAssignment: (bn: number, unit: string, seat: string) => { display_name: string; member_id: number } | undefined;
  getPendingChange: (bn: number, unit: string, seat: string) => { proposed_member_name: string | null } | undefined;
  canEdit: boolean;
  onEditSeat: (unitId: string, seatId: string) => void;
  filterStation: string;
  filterUnitType: string;
  searchQuery: string;
  memberPhotos: Map<number, string>;
}) {
  const bnColor = BATTALION_COLORS[battalion.id] ?? '#999';
  const bnBg = BATTALION_BG_COLORS[battalion.id] ?? 'transparent';

  // Apply unit type + search filters to a list of units
  const filterUnits = (units: UnitDef[]) =>
    units
      .filter(isApparatusUnit)
      .filter((u) => !filterUnitType || u.type === filterUnitType)
      .filter((u) => unitMatchesSearch(u, battalion.id, searchQuery, getAssignment));

  // Count filled seats (only apparatus units — exclude utility/safety)
  let totalSeats = 0;
  let filledSeats = 0;
  for (const station of battalion.stations) {
    for (const unit of station.units.filter(isApparatusUnit)) {
      for (const seat of unit.seats) {
        totalSeats++;
        if (getAssignment(battalion.id, unit.id, seat.id)) filledSeats++;
      }
    }
  }
  for (const unit of battalion.commandUnits.filter(isApparatusUnit)) {
    for (const seat of unit.seats) {
      totalSeats++;
      if (getAssignment(battalion.id, unit.id, seat.id)) filledSeats++;
    }
  }

  // Filter stations
  const filteredStations = battalion.stations.filter((st) => !filterStation || st.id === filterStation);

  // Filter command units (shown when no station filter, or when it's buggy type match)
  const filteredCommandUnits = filterStation ? [] : filterUnits(battalion.commandUnits);

  return (
    <div
      className="bg-surface-raised rounded-xl border border-edge-default overflow-hidden"
      style={{ borderColor: expanded ? bnColor + '40' : undefined }}
    >
      {/* Battalion header */}
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-3 px-4 py-3 cursor-pointer
                   hover:brightness-110 transition-all duration-150 text-left border-none"
        style={{ backgroundColor: bnBg }}
      >
        {expanded ? (
          <ChevronDown className="h-4 w-4 shrink-0" style={{ color: bnColor }} />
        ) : (
          <ChevronRight className="h-4 w-4 shrink-0" style={{ color: bnColor }} />
        )}
        <div
          className="w-7 h-7 rounded-lg flex items-center justify-center font-black text-sm"
          style={{ backgroundColor: bnColor + '20', color: bnColor }}
        >
          {battalion.id}
        </div>
        <span className="text-sm font-bold" style={{ color: bnColor }}>
          {battalion.label}
        </span>
        <span className="text-xs text-content-tertiary ml-auto">
          {filledSeats}/{totalSeats} seats filled
        </span>
      </button>

      {/* Expanded content — Apparatus views */}
      {expanded && (
        <div className="p-3 space-y-5">
          {/* Command section (BC buggy only — safety/utility excluded) */}
          {filteredCommandUnits.length > 0 && (
            <div>
              <div className="text-[11px] font-bold text-content-tertiary uppercase tracking-widest px-1 mb-3">
                Command
              </div>
              <div className="flex flex-wrap gap-3 justify-center lg:justify-start">
                {filteredCommandUnits.map((unit) => (
                  <ApparatusCard
                    key={unit.id}
                    unit={unit}
                    battalionId={battalion.id}
                    battalionColor={bnColor}
                    getAssignment={getAssignment}
                    getPendingChange={getPendingChange}
                    canEdit={canEdit}
                    onEditSeat={onEditSeat}
                    memberPhotos={memberPhotos}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Stations */}
          {filteredStations.map((station) => {
            const apparatusUnits = filterUnits(station.units);
            if (apparatusUnits.length === 0) return null;
            return (
              <div key={station.id}>
                <div className="flex items-center gap-2 px-1 mb-3">
                  <MapPin className="h-3.5 w-3.5 text-content-tertiary" />
                  <span className="text-[11px] font-bold text-content-tertiary uppercase tracking-widest">
                    {station.label}
                  </span>
                </div>
                <div className="flex flex-wrap gap-3 justify-center lg:justify-start">
                  {apparatusUnits.map((unit) => (
                    <ApparatusCard
                      key={unit.id}
                      unit={unit}
                      battalionId={battalion.id}
                      battalionColor={bnColor}
                      getAssignment={getAssignment}
                      getPendingChange={getPendingChange}
                      canEdit={canEdit}
                      onEditSeat={onEditSeat}
                      memberPhotos={memberPhotos}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Edit Seat Modal ──────────────────────────────────────────────────

function EditSeatModal({
  battalionId,
  unitId,
  seatId,
  currentAssignment,
  members,
  selectedMemberId,
  onSelectMember,
  notes,
  onNotesChange,
  submitting,
  onSubmit,
  onClose,
}: {
  battalionId: number;
  unitId: string;
  seatId: string;
  currentAssignment: { display_name: string; member_id: number } | undefined;
  members: { id: number; display_name: string; email: string; battalion_id: number | null }[];
  selectedMemberId: string;
  onSelectMember: (id: string) => void;
  notes: string;
  onNotesChange: (notes: string) => void;
  submitting: boolean;
  onSubmit: () => void;
  onClose: () => void;
}) {
  const bnColor = BATTALION_COLORS[battalionId] ?? '#999';

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-surface-raised rounded-xl border border-edge-default shadow-2xl w-full max-w-md overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 bg-surface-sunken border-b border-edge-muted">
          <div>
            <div className="text-sm font-bold text-content-primary">Propose Assignment Change</div>
            <div className="text-[11px] text-content-tertiary mt-0.5">
              <span style={{ color: bnColor }}>Bn {battalionId}</span> &middot; {unitId} &middot; {seatId}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-surface-base cursor-pointer transition-colors border-none bg-transparent"
          >
            <X className="h-4 w-4 text-content-secondary" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* Current assignment */}
          {currentAssignment && (
            <div>
              <label className="text-[11px] font-bold text-content-tertiary uppercase tracking-wider">
                Current Assignment
              </label>
              <div className="mt-1 flex items-center gap-2 px-3 py-2 bg-surface-sunken rounded-lg">
                <User className="h-3.5 w-3.5 text-content-secondary" />
                <span className="text-sm text-content-primary">{currentAssignment.display_name}</span>
              </div>
            </div>
          )}

          {/* Member selector */}
          <div>
            <label className="text-[11px] font-bold text-content-tertiary uppercase tracking-wider">
              New Assignment
            </label>
            <select
              value={selectedMemberId}
              onChange={(e) => onSelectMember(e.target.value)}
              className="mt-1 w-full px-3 py-2 bg-surface-base border border-edge-default rounded-lg
                         text-sm text-content-primary focus:outline-none focus:ring-1 focus:ring-action-primary"
            >
              <option value="">-- Clear seat (leave vacant) --</option>
              {members.map((m) => (
                <option key={m.id} value={String(m.id)}>
                  {m.display_name}
                  {m.battalion_id ? ` (Bn ${m.battalion_id})` : ''}
                </option>
              ))}
            </select>
          </div>

          {/* Notes */}
          <div>
            <label className="text-[11px] font-bold text-content-tertiary uppercase tracking-wider">
              Notes (optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => onNotesChange(e.target.value)}
              placeholder="Reason for change..."
              rows={2}
              className="mt-1 w-full px-3 py-2 bg-surface-base border border-edge-default rounded-lg
                         text-sm text-content-primary resize-none focus:outline-none focus:ring-1 focus:ring-action-primary"
            />
          </div>

          {/* Info banner */}
          <div className="flex items-start gap-2 px-3 py-2 bg-action-primary/5 border border-action-primary/20 rounded-lg">
            <ClipboardList className="h-3.5 w-3.5 text-action-primary shrink-0 mt-0.5" />
            <p className="text-[11px] text-content-secondary leading-relaxed">
              This change will be submitted for Fire Chief approval before taking effect.
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 text-sm font-medium text-content-secondary
                         bg-surface-sunken border border-edge-default rounded-lg cursor-pointer
                         hover:bg-surface-base transition-colors duration-150"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onSubmit}
              disabled={submitting}
              className="flex-1 px-4 py-2 text-sm font-semibold text-white
                         bg-action-primary rounded-lg cursor-pointer hover:brightness-110
                         transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed border-none"
            >
              {submitting ? 'Submitting...' : 'Submit for Approval'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Pending Changes Panel ────────────────────────────────────────────

function PendingChangesPanel({
  changes,
  isAdmin,
  onApprove,
  onReject,
  onClose,
}: {
  changes: {
    id: number;
    battalion_id: number;
    unit_id: string;
    seat_id: string;
    proposed_member_name: string | null;
    current_member_name: string | null;
    requester_name: string;
    notes: string | null;
    created_at: string;
  }[];
  isAdmin: boolean;
  onApprove: (id: number) => Promise<void>;
  onReject: (id: number, notes?: string) => Promise<void>;
  onClose: () => void;
}) {
  return (
    <div className="mb-4 bg-surface-raised rounded-xl border border-warning-500/30 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 bg-warning-500/10 border-b border-warning-500/20">
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4 text-warning-400" />
          <span className="text-sm font-bold text-warning-400">Pending Roster Changes</span>
          <span className="text-[10px] text-content-tertiary">({changes.length})</span>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="p-1 rounded hover:bg-surface-sunken cursor-pointer border-none bg-transparent"
        >
          <X className="h-3.5 w-3.5 text-content-secondary" />
        </button>
      </div>
      <div className="divide-y divide-edge-muted max-h-[300px] overflow-y-auto">
        {changes.map((change) => (
          <div key={change.id} className="px-4 py-3">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <span
                  className="text-[10px] font-bold px-1.5 py-0.5 rounded"
                  style={{
                    color: BATTALION_COLORS[change.battalion_id],
                    backgroundColor: BATTALION_COLORS[change.battalion_id] + '15',
                  }}
                >
                  Bn {change.battalion_id}
                </span>
                <span className="text-xs font-semibold text-content-primary">
                  {change.unit_id} &middot; {change.seat_id}
                </span>
              </div>
              <span className="text-[10px] text-content-tertiary">
                {new Date(change.created_at).toLocaleDateString()}
              </span>
            </div>
            <div className="text-xs text-content-secondary mb-1">
              {change.current_member_name ? (
                <>
                  <span className="text-content-tertiary">{change.current_member_name}</span>
                  {' → '}
                </>
              ) : (
                'Vacant → '
              )}
              <span className="font-medium text-content-primary">{change.proposed_member_name ?? 'Clear seat'}</span>
            </div>
            <div className="text-[10px] text-content-tertiary">
              Requested by {change.requester_name}
              {change.notes && <> &middot; {change.notes}</>}
            </div>

            {/* Admin approve/reject buttons */}
            {isAdmin && (
              <div className="flex gap-2 mt-2">
                <button
                  type="button"
                  onClick={() => onApprove(change.id)}
                  className="flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold
                             text-green-400 bg-green-500/10 border border-green-500/20
                             rounded-md cursor-pointer hover:bg-green-500/20 transition-colors border-none"
                >
                  <Check className="h-3 w-3" />
                  Approve
                </button>
                <button
                  type="button"
                  onClick={() => onReject(change.id)}
                  className="flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold
                             text-danger-400 bg-danger-500/10 border border-danger-500/20
                             rounded-md cursor-pointer hover:bg-danger-500/20 transition-colors border-none"
                >
                  <X className="h-3 w-3" />
                  Reject
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
